<template>
  <div class="business-crud">
    <div class="toolbar">
      <template v-for="field in searchFields">
        <a-date-picker
          v-if="field.type === 'date'"
          :key="field.key"
          v-model="query[field.key]"
          value-format="YYYY-MM-DD"
          :placeholder="field.label"
          class="search-item"
        />
        <a-input
          v-else-if="field.type !== 'select'"
          :key="field.key"
          v-model="query[field.key]"
          :placeholder="field.label"
          class="search-item"
          allow-clear
        />
        <a-select
          v-else
          :key="field.key"
          v-model="query[field.key]"
          :placeholder="field.label"
          class="search-item"
          allow-clear
        >
          <a-select-option v-for="option in field.options" :key="option.value" :value="option.value">
            {{ option.label }}
          </a-select-option>
        </a-select>
      </template>
      <a-button type="primary" icon="search" @click="handleSearch">查询</a-button>
      <a-button icon="reload" class="ml8" @click="handleReset">重置</a-button>
      <a-button v-if="allowCreate" type="primary" icon="plus" class="ml8" @click="handleCreate">新增</a-button>
      <a-button v-if="exportApi" icon="download" class="ml8" :loading="exporting" @click="handleExport">导出Excel</a-button>
    </div>

    <a-table
      :columns="tableColumns"
      :data-source="data"
      rowKey="id"
      :loading="loading"
      :pagination="pagination"
      @change="handleTableChange"
    >
      <span slot="status" slot-scope="text">
        <a-tag :color="isEnabled(text) ? 'green' : 'orange'">{{ formatValue(text) }}</a-tag>
      </span>
      <span slot="preview" slot-scope="text, record">
        <slot name="preview" :text="text" :record="record">{{ text }}</slot>
      </span>
      <span slot="action" slot-scope="text, record">
        <a @click="handleEdit(record)">编辑</a>
        <a-divider v-if="allowDelete" type="vertical" />
        <a-popconfirm v-if="allowDelete" title="确定删除吗？" @confirm="handleDelete(record.id)">
          <a class="danger-link">删除</a>
        </a-popconfirm>
      </span>
    </a-table>

    <a-modal
      v-model="visible"
      :title="form.id ? '编辑' + title : '新增' + title"
      :width="modalWidth"
      @ok="handleOk"
    >
      <a-form-model :model="form" :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-form-model-item
          v-for="field in formFields"
          v-show="!field.visible || field.visible(form)"
          :key="field.key"
          :label="field.label"
        >
          <a-textarea
            v-if="field.type === 'textarea'"
            v-model="form[field.key]"
            :rows="field.rows || 4"
            :placeholder="field.placeholder || field.label"
          />
          <div v-else-if="field.type === 'richtext'" class="richtext-wrap">
            <quill-editor
              v-model="form[field.key]"
              :options="editorOptions"
            />
          </div>
          <div v-else-if="field.type === 'upload-image'" class="upload-image-field">
            <a-upload
              name="file"
              list-type="picture-card"
              :file-list="getUploadFileList(field.key)"
              :action="uploadAction"
              :headers="uploadHeaders"
              :before-upload="beforeImageUpload"
              @change="info => handleImageChange(info, field.key)"
            >
              <div v-if="!form[field.key]">
                <a-icon type="plus" />
                <div class="upload-text">上传图片</div>
              </div>
            </a-upload>
          </div>
          <div v-else-if="field.type === 'upload-video'" class="upload-file-field">
            <a-upload
              name="file"
              :file-list="getUploadFileList(field.key)"
              :action="videoUploadAction"
              :headers="uploadHeaders"
              accept="video/mp4,video/webm,video/ogg,video/quicktime,.m4v"
              :before-upload="beforeVideoUpload"
              @change="info => handleFileChange(info, field.key)"
            >
              <a-button><a-icon type="upload" /> 上传视频</a-button>
            </a-upload>
          </div>
          <div v-else-if="field.type === 'upload-file'" class="upload-file-field">
            <a-upload
              name="file"
              :file-list="getUploadFileList(field.key)"
              :action="fileUploadAction"
              :headers="uploadHeaders"
              :accept="field.accept || '.pdf,image/*'"
              :before-upload="beforeDocumentUpload"
              @change="info => handleDocumentChange(info, field.key)"
            >
              <a-button><a-icon type="upload" /> 上传文件</a-button>
            </a-upload>
          </div>
          <a-input-number
            v-else-if="field.type === 'number'"
            v-model="form[field.key]"
            :min="field.min"
            :precision="field.precision"
            style="width: 100%;"
          />
          <a-date-picker
            v-else-if="field.type === 'datetime'"
            v-model="form[field.key]"
            show-time
            value-format="YYYY-MM-DD HH:mm:ss"
            :input-read-only="true"
            :placeholder="field.placeholder || '请选择' + field.label"
            style="width: 100%;"
          />
          <a-date-picker
            v-else-if="field.type === 'date'"
            v-model="form[field.key]"
            value-format="YYYY-MM-DD"
            :input-read-only="true"
            :placeholder="field.placeholder || '请选择' + field.label"
            style="width: 100%;"
          />
          <a-switch
            v-else-if="field.type === 'switch'"
            :checked="form[field.key] === 1"
            @change="val => form[field.key] = val ? 1 : 0"
          />
          <a-select
            v-else-if="field.type === 'select'"
            v-model="form[field.key]"
            :placeholder="field.placeholder || field.label"
            allow-clear
          >
            <a-select-option v-for="option in field.options" :key="option.value" :value="option.value">
              {{ option.label }}
            </a-select-option>
          </a-select>
          <a-input
            v-else
            v-model="form[field.key]"
            :placeholder="field.placeholder || field.label"
          />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import { API_BASE_URL } from '@/api'
import adminSession from '@/utils/adminSession'
import { downloadBlobResponse } from '@/utils/downloadFile'

export default {
  name: 'BusinessCrud',
  components: { quillEditor },
  props: {
    title: { type: String, required: true },
    columns: { type: Array, required: true },
    searchFields: { type: Array, default: () => [] },
    formFields: { type: Array, required: true },
    listApi: { type: Function, required: true },
    detailApi: { type: Function, default: null },
    saveApi: { type: Function, required: true },
    deleteApi: { type: Function, required: true },
    defaults: { type: Object, default: () => ({}) },
    modalWidth: { type: Number, default: 720 },
    allowCreate: { type: Boolean, default: true },
    allowDelete: { type: Boolean, default: true },
    exportApi: { type: Function, default: null },
    exportFileName: { type: String, default: '导出数据.xlsx' }
  },
  data() {
    return {
      query: {},
      data: [],
      loading: false,
      visible: false,
      exporting: false,
      coverGenerating: false,
      videoSourceFile: null,
      form: {},
      uploadFileMap: {},
      pagination: { current: 1, pageSize: 10, total: 0 },
      uploadAction: API_BASE_URL + '/upload/image',
      fileUploadAction: API_BASE_URL + '/upload/file',
      videoUploadAction: API_BASE_URL + '/upload/video',
      editorOptions: {
        placeholder: '请输入内容',
        theme: 'snow'
      }
    }
  },
  computed: {
    uploadHeaders() {
      const token = adminSession.getAdminToken()
      return token ? { Authorization: 'Bearer ' + token } : {}
    },
    tableColumns() {
      return this.columns.concat([
        { title: '操作', key: 'action', width: 140, scopedSlots: { customRender: 'action' } }
      ])
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      const params = {
        ...this.query,
        page: this.pagination.current,
        pageSize: this.pagination.pageSize
      }
      this.listApi(params).then(res => {
        const pageData = res.data || {}
        this.data = pageData.records || pageData.list || []
        this.pagination.total = pageData.total || 0
      }).finally(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    handleReset() {
      this.query = {}
      this.handleSearch()
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    },
    handleCreate() {
      this.form = { ...this.defaults }
      this.uploadFileMap = {}
      this.videoSourceFile = null
      this.visible = true
    },
    async handleEdit(record) {
      this.uploadFileMap = {}
      this.videoSourceFile = null
      if (!this.detailApi) {
        this.form = { ...record }
        this.visible = true
        return
      }
      try {
        const res = await this.detailApi(record.id)
        this.form = { ...record, ...(res.data || {}) }
        this.visible = true
      } catch (error) {
        this.$message.error((error && error.message) || '详情加载失败')
      }
    },
    handleOk() {
      if (this.coverGenerating) {
        this.$message.info('正在截取视频第10帧，请稍候')
        return
      }
      if (this.form.videoUrl && !this.form.coverImage) {
        if (this.videoSourceFile) this.generateVideoCover(this.videoSourceFile)
        else this.$message.warning('请上传封面图，或重新上传视频以自动截取第10帧')
        return
      }
      this.saveApi(this.form).then(() => {
        this.$message.success('保存成功')
        this.visible = false
        this.loadData()
      })
    },
    handleDelete(id) {
      this.deleteApi({ id }).then(() => {
        this.$message.success('删除成功')
        this.loadData()
      })
    },
    async handleExport() {
      this.exporting = true
      try {
        const response = await this.exportApi({ ...this.query })
        await downloadBlobResponse(response, this.exportFileName)
        this.$message.success('导出成功')
      } catch (error) {
        this.$message.error(error.message || '导出失败')
      } finally {
        this.exporting = false
      }
    },
    beforeImageUpload(file) {
      const isImage = file.type.indexOf('image/') === 0
      if (!isImage) {
        this.$message.error('仅支持上传图片')
      }
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        this.$message.error('图片大小不能超过5MB')
      }
      return isImage && isLt5M
    },
    beforeVideoUpload(file) {
      const suffix = String(file.name || '').toLowerCase().split('.').pop()
      const isVideo = ['mp4', 'webm', 'ogg', 'mov', 'm4v'].indexOf(suffix) !== -1
      if (!isVideo) this.$message.error('仅支持 MP4、WebM、OGG、MOV、M4V 视频')
      const isLt200M = file.size / 1024 / 1024 <= 200
      if (!isLt200M) this.$message.error('视频大小不能超过200MB')
      if (isVideo && isLt200M) this.videoSourceFile = file
      return isVideo && isLt200M
    },
    beforeDocumentUpload(file) {
      const suffix = String(file.name || '').toLowerCase().split('.').pop()
      const isAllowed = ['pdf', 'jpg', 'jpeg', 'png'].indexOf(suffix) !== -1
      if (!isAllowed) this.$message.error('发票文件仅支持 PDF、JPG、JPEG、PNG 格式')
      const isLt20M = file.size / 1024 / 1024 <= 20
      if (!isLt20M) this.$message.error('文件大小不能超过20MB')
      return isAllowed && isLt20M
    },
    getUploadFileList(key) {
      if (this.uploadFileMap[key]) return this.uploadFileMap[key]
      if (!this.form[key]) return []
      return [{
        uid: key,
        name: '已上传文件',
        status: 'done',
        url: this.normalizeUploadAssetUrl(this.form[key])
      }]
    },
    toStorageAssetUrl(url) {
      const value = String(url || '').trim()
      const uploadIndex = value.indexOf('/uploads/')
      return uploadIndex >= 0 ? value.slice(uploadIndex).split(/[?#]/)[0] : value
    },
    normalizeUploadAssetUrl(url) {
      const value = String(url || '').trim()
      const uploadIndex = value.indexOf('/uploads/')
      if (uploadIndex >= 0) return API_BASE_URL.replace(/\/$/, '') + value.slice(uploadIndex).split(/[?#]/)[0]
      if (value.indexOf('/static/file/') === 0) return value.replace('/static/file/', '/')
      if (value.indexOf('static/file/') === 0) return value.replace('static/file/', '/')
      if (value.indexOf('file/') === 0) return value.replace('file/', '/')
      return value
    },
    handleImageChange(info, key) {
      this.$set(this.uploadFileMap, key, info.fileList.slice(-1))
      if (info.file.status === 'removed') {
        this.$set(this.form, key, '')
        this.$set(this.uploadFileMap, key, [])
      } else if (info.file.status === 'done') {
        let response = info.file.response || {}
        if (typeof response === 'string') {
          try {
            response = JSON.parse(response || '{}')
          } catch (e) {
            response = {}
          }
        }
        if (response.code !== 200) {
          this.$message.error(response.msg || '上传失败')
          return
        }
        const data = response.data || {}
        const storageUrl = this.toStorageAssetUrl(data.path || data.url)
        if (!storageUrl) {
          this.$message.error('上传成功但未返回图片地址')
          return
        }
        this.$set(this.form, key, storageUrl)
        this.$set(this.uploadFileMap, key, [{
          uid: info.file.uid,
          name: info.file.name,
          status: 'done',
          url: this.normalizeUploadAssetUrl(storageUrl)
        }])
        this.$message.success('上传成功')
      } else if (info.file.status === 'error') {
        this.$message.error('上传失败')
      }
    },
    handleFileChange(info, key) {
      this.$set(this.uploadFileMap, key, info.fileList.slice(-1))
      if (info.file.status === 'removed') {
        this.$set(this.form, key, '')
        this.$set(this.uploadFileMap, key, [])
      } else if (info.file.status === 'done') {
        const response = info.file.response || {}
        if (response.code !== 200) {
          this.$message.error(response.msg || '视频上传失败')
          return
        }
        const data = response.data || {}
        const storageUrl = this.toStorageAssetUrl(data.path || data.url)
        if (!storageUrl) {
          this.$message.error('上传成功但未返回视频地址')
          return
        }
        this.$set(this.form, key, storageUrl)
        this.videoSourceFile = info.file.originFileObj || this.videoSourceFile
        this.$set(this.uploadFileMap, key, [{ uid: info.file.uid, name: info.file.name, status: 'done', url: this.normalizeUploadAssetUrl(storageUrl) }])
        this.$message.success('视频上传成功')
        if (!this.form.coverImage && this.videoSourceFile) {
          this.generateVideoCover(this.videoSourceFile)
        }
      } else if (info.file.status === 'error') {
        this.$message.error('视频上传失败')
      }
    },
    handleDocumentChange(info, key) {
      this.$set(this.uploadFileMap, key, info.fileList.slice(-1))
      if (info.file.status === 'removed') {
        this.$set(this.form, key, '')
        this.$set(this.uploadFileMap, key, [])
      } else if (info.file.status === 'done') {
        const response = info.file.response || {}
        if (response.code !== 200) {
          this.$message.error(response.msg || '文件上传失败')
          return
        }
        const data = response.data || {}
        const storageUrl = this.toStorageAssetUrl(data.path || data.url)
        if (!storageUrl) {
          this.$message.error('上传成功但未返回文件地址')
          return
        }
        this.$set(this.form, key, storageUrl)
        this.$set(this.uploadFileMap, key, [{ uid: info.file.uid, name: info.file.name, status: 'done', url: this.normalizeUploadAssetUrl(storageUrl) }])
        this.$message.success('文件上传成功')
      } else if (info.file.status === 'error') {
        this.$message.error('文件上传失败')
      }
    },
    async generateVideoCover(file) {
      this.coverGenerating = true
      this.$message.info('未上传封面，正在截取视频第10帧')
      try {
        const blob = await this.captureTenthFrame(file)
        const formData = new FormData()
        formData.append('file', blob, 'video-cover.jpg')
        const response = await fetch(this.uploadAction, { method: 'POST', headers: this.uploadHeaders, body: formData })
        const result = await response.json()
        if (result.code !== 200 || !result.data) throw new Error(result.msg || '封面上传失败')
        const storageUrl = this.toStorageAssetUrl(result.data.path || result.data.url)
        if (!storageUrl) throw new Error('封面上传成功但未返回地址')
        this.$set(this.form, 'coverImage', storageUrl)
        this.$set(this.uploadFileMap, 'coverImage', [{ uid: 'auto-video-cover', name: '视频第10帧封面', status: 'done', url: this.normalizeUploadAssetUrl(storageUrl) }])
        this.$message.success('已自动生成视频封面')
      } catch (error) {
        this.$message.warning(error.message || '视频封面自动生成失败，请手动上传封面')
      } finally {
        this.coverGenerating = false
      }
    },
    captureTenthFrame(file) {
      return new Promise((resolve, reject) => {
        const video = document.createElement('video')
        const objectUrl = URL.createObjectURL(file)
        let finished = false
        let callbackCount = 0
        const timer = window.setTimeout(() => finish(new Error('读取视频帧超时')), 12000)
        const cleanup = () => {
          window.clearTimeout(timer)
          video.pause()
          video.removeAttribute('src')
          URL.revokeObjectURL(objectUrl)
        }
        const finish = error => {
          if (finished) return
          finished = true
          cleanup()
          if (error) reject(error)
        }
        const capture = () => {
          if (finished) return
          const sourceWidth = video.videoWidth || 1280
          const sourceHeight = video.videoHeight || 720
          const scale = Math.min(1, 1280 / sourceWidth)
          const canvas = document.createElement('canvas')
          canvas.width = Math.max(1, Math.round(sourceWidth * scale))
          canvas.height = Math.max(1, Math.round(sourceHeight * scale))
          canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
          canvas.toBlob(blob => {
            if (!blob) {
              finish(new Error('视频帧转换失败'))
              return
            }
            finished = true
            cleanup()
            resolve(blob)
          }, 'image/jpeg', 0.88)
        }
        const seekFallback = () => {
          video.pause()
          video.onseeked = capture
          video.currentTime = Math.min(Math.max(video.duration || 0, 0), 10 / 30)
        }
        video.muted = true
        video.playsInline = true
        video.preload = 'auto'
        video.onerror = () => finish(new Error('视频文件读取失败'))
        video.onloadeddata = () => {
          if (typeof video.requestVideoFrameCallback !== 'function') {
            seekFallback()
            return
          }
          const nextFrame = () => {
            video.requestVideoFrameCallback((...frameArgs) => {
              const metadata = frameArgs[1]
              callbackCount += 1
              if ((metadata && metadata.presentedFrames >= 10) || callbackCount >= 10) capture()
              else nextFrame()
            })
          }
          video.play().then(nextFrame).catch(seekFallback)
        }
        video.src = objectUrl
      })
    },
    isEnabled(value) {
      return value === 1 || value === '已完成' || value === '已支付' || value === '已开票' || value === '已报价'
    },
    formatValue(value) {
      if (value === 1) return '启用'
      if (value === 0) return '停用'
      return value
    }
  }
}
</script>

<style scoped>
.business-crud {
  padding: 20px;
  background: #fff;
}
.toolbar {
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.search-item {
  width: 200px;
}
.ml8 {
  margin-left: 0;
}
.danger-link {
  color: #f5222d;
}
.upload-text {
  margin-top: 8px;
}
.richtext-wrap {
  line-height: normal;
}
.richtext-wrap >>> .ql-container {
  min-height: 220px;
}
</style>
